import { ColumnDef } from '@tanstack/react-table';
import 'react-datepicker/dist/react-datepicker.css';

import HaggleMonthPickerComponent from '../common/DatePicker';
import Table from '../common/Table';
import TableHeader from '../common/TableHeader';
import { IPaginationTable } from '../common/interfaces/IPaginationTable';

import { haggleEventFilterList } from '@/components/utils/constants';
import { formattedMonth } from '@/components/utils/utils';
import { ICollection } from '@/interfaces/api/collection/ICollection';
import {
	IHaggleEventMonthlySalesReport,
	IHaggleEventSalesReport,
} from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';

interface HaggleEventsAdminTableProps {
	collectionHaggleEvent: IHaggleEventMonthlySalesReport;
	paginationOption: IPaginationTable;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnDef<IHaggleEventSalesReport, any>[];
	merchants: number;
	refetch: () => void;
	query: string;
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	setFilterSelected: React.Dispatch<React.SetStateAction<string>>;
	handleDateChange: (newDate: Date | null) => void;
	selectedPickerDate: Date;
	reportDateString: string;
	totalSalesMade: IHaggleEventMonthlySalesReport['data']['totalSalesMade'];
	totalProfit: IHaggleEventMonthlySalesReport['data']['totalProfit'];
	totalDeals: IHaggleEventMonthlySalesReport['data']['totalDeals'];
}

const HaggleAdminMonthlySalesReportTable = ({
	collectionHaggleEvent,
	paginationOption,
	columns,
	merchants,
	refetch,
	query,
	setQuery,
	setFilterSelected,
	handleDateChange,
	selectedPickerDate,
	reportDateString,
	totalSalesMade,
	totalProfit,
	totalDeals,
}: HaggleEventsAdminTableProps) => {
	const handleCollectionWithAction = (
		collection: IHaggleEventMonthlySalesReport,
	): ICollection<IHaggleEventSalesReport> => {
		return {
			...collection,
			data: collection.data.events.map((item) => ({
				...item,
			})),
		};
	};

	return (
		<div className="flex flex-col w-full">
			<div className="w-full bg-white rounded-md shadow p-3 mb-4 border border-gray-300 sm:p-4 md:p-6">
				<div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center">
					<h2 className="text-lg font-semibold sm:text-xl">
						{formattedMonth(reportDateString)} Haggle Event Summary
					</h2>
					<div className="flex gap-2">
						<HaggleMonthPickerComponent
							selectedDate={selectedPickerDate}
							onChange={handleDateChange}
							dateFormat="MMMM yyyy"
							showMonthYearPicker
							className="p-2 border border-gray-300 rounded-md cursor-pointer text-sm"
							ariaLabel="Select month and year for the report"
						/>
					</div>
				</div>

				<div className="p-3 bg-blue-50 rounded-md">
					<h3 className="font-semibold mb-2 text-sm sm:text-base">
						Sales Summary
					</h3>
					<div className="space-y-1 text-sm sm:text-base">
						<p>
							Total sales from completed transactions:{' '}
							<span className="font-semibold">
								${totalSalesMade?.toFixed(2)}
							</span>
						</p>
						<p>
							Total profit from completed transactions:{' '}
							<span className="font-semibold">${totalProfit?.toFixed(2)}</span>
						</p>
						<p>
							Total sales completed:{' '}
							<span className="font-semibold">{totalDeals}</span>
						</p>
					</div>
				</div>
			</div>

			<TableHeader
				title=""
				counts={[
					{
						label: 'Total Haggle Events',
						value: collectionHaggleEvent.itemCount,
						dataTest: 'total-haggle-events-count',
					},
					{
						label: 'Total Merchants',
						value: merchants,
						dataTest: 'total-merchants-count',
					},
				]}
				searchConfigs={[
					{
						query: query,
						setQuery: setQuery,
						placeholder: 'Search by id or store',
						refetch: refetch,
					},
				]}
				filterConfig={{
					setFilterSelected: setFilterSelected,
					filterList: haggleEventFilterList,
				}}
			/>
			<div className="bg-grey-0 border border-gray-300 rounded-xl overflow-x-auto">
				<Table<IHaggleEventSalesReport>
					collection={handleCollectionWithAction(collectionHaggleEvent)}
					columns={columns}
					paginationSection={paginationOption}
					tableStyles={{
						tableBox: 'w-full overflow-y-auto h-fit',
						table: 'w-full',
					}}
					dataTest="haggle-events-admin-table"
				/>
			</div>
		</div>
	);
};

export default HaggleAdminMonthlySalesReportTable;
